import axios from 'axios';

// Build-time override (optional): REACT_APP_API_URL=https://api.example.com/api
// Production default: same-origin /api when the SPA and Laravel share the domain.
const API_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? '/api'
    : 'http://127.0.0.1:8000/api');


const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Add request interceptor for authentication
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// API endpoints
export const truckService = {
    getAll: () => api.get('/trucks'),
    create: (data) => api.post('/trucks', data),
    update: (id, data) => api.put(`/trucks/${id}`, data),
    delete: (id) => api.delete(`/trucks/${id}`),
    restore: (id) => api.post(`/trucks/${id}/restore`)
};

export const tripService = {
    getAll: () => api.get('/trips'),
    create: (data) => api.post('/trips', data),
    update: (id, data) => api.put(`/trips/${id}`, data),
    delete: (id) => api.delete(`/trips/${id}`)
};

export const expenseService = {
    getAll: () => api.get('/expenses'),
    create: (data) => api.post('/expenses', data),
    update: (id, data) => api.put(`/expenses/${id}`, data),
    delete: (id) => api.delete(`/expenses/${id}`)
};

export const reportService = {
    getProfitLoss: () => api.get('/reports/profit-loss'),
    getDriverReports: () => api.get('/reports/driver'),
    getTruckReports: () => api.get('/reports/truck')
};

export const driverService = {
    getAll: () => api.get('/drivers'),
    create: (data) => api.post('/drivers', data),
    update: (id, data) => api.put(`/drivers/${id}`, data),
    delete: (id) => api.delete(`/drivers/${id}`),
    getById: (id) => api.get(`/drivers/${id}`)
};

export const driverReportService = {
    getAll: (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.driver_id) params.append('driver_id', filters.driver_id);
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        
        const queryString = params.toString();
        return api.get(`/reports/driver-details${queryString ? `?${queryString}` : ''}`);
    }
};

export const tripProfitLossService = {
    getAll: (filters = {}) => {
        const params = new URLSearchParams();
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        if (filters.driver_id) params.append('driver_id', filters.driver_id);
        if (filters.truck_id) params.append('truck_id', filters.truck_id);
        
        const queryString = params.toString();
        return api.get(`/reports/trip-profit-loss${queryString ? `?${queryString}` : ''}`);
    }
};

export const truckExpenseService = {
    getAll: (filters = {}) => {
        const queryParams = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value) queryParams.append(key, value);
        });
        return api.get(`/truck-expenses${queryParams.toString() ? `?${queryParams.toString()}` : ''}`);
    },
    getTrucks: () => api.get('/trucks'),
    create: (data) => api.post('/truck-expenses', data),
    update: (id, data) => api.put(`/truck-expenses/${id}`, data),
    delete: (id) => api.delete(`/truck-expenses/${id}`),
    getEditData: (id) => api.get(`/truck-expenses/${id}/edit`)
};

export const loadDetailService = {
    getAll: () => api.get('/load-details'),
    create: (data) => api.post('/load-details', data),
    update: (id, data) => api.put(`/load-details/${id}`, data),
    delete: (id) => api.delete(`/load-details/${id}`),
    getById: (id) => api.get(`/load-details/${id}`)
};

export const driverSalaryService = {
    getAll: () => api.get('/driver-salaries'),
    create: (data) => api.post('/driver-salaries', data),
    update: (id, data) => api.put(`/driver-salaries/${id}`, data),
    delete: (id) => api.delete(`/driver-salaries/${id}`),
    getById: (id) => api.get(`/driver-salaries/${id}`)
};

export const authService = {
    login: (username, password) => api.post('/login', { username, password }),
    logout: () => api.post('/logout'),
    me: () => api.get('/me'),
    forgotPassword: (email) => api.post('/forgot-password', { email }),
    resetPassword: (email, token, password, passwordConfirmation) => api.post('/reset-password', {
        email,
        token,
        password,
        password_confirmation: passwordConfirmation
    })
};

export default api; 